<template>
  <div class="sp-target-selector-app">
    <div v-show="mode === 'url'" class="sp-target-selector-app__pane" part="input">
      <TargetSelectorActions :actions="actions" @activate="runAction" />
      <slot name="input"></slot>
    </div>

    <div v-show="mode === 'search'" class="sp-target-selector-app__pane" part="search">
      <TargetSelectorActions :actions="actions" @activate="runAction" />
      <slot name="search"></slot>
    </div>

    <div v-show="mode === 'preview'" class="sp-target-selector-app__pane" part="preview">
      <TargetSelectorActions :actions="actions" @activate="runAction" />
      <slot name="preview"></slot>
      <TargetSelectorPreview :icon-name="preview.iconName" :label="preview.label" :url="previewUrl" />
    </div>
  </div>
</template>

<script setup>
import { useEventListener, watchImmediate } from "@vueuse/core";
import { computed, onMounted, shallowRef } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { toBoolean } from "#ce/utils/props";
import { useHostElement } from "#ce/composables/useHostElement";
import TargetSelectorActions from "./components/TargetSelectorActions.ce.vue";
import TargetSelectorPreview from "./components/TargetSelectorPreview.ce.vue";
import { useTargetPreview } from "./composables/useTargetPreview";
import { useTargetSelection } from "./composables/useTargetSelection";
import { props as appProps } from "./props.js";

const emit = defineEmits(["select-target", "remove", "show-search", "show-custom-url"]);

const props = defineProps(appProps);

const host = useHostElement();

function lightDomInput(selector) {
  return host?.querySelector(selector) ?? null;
}

// A form that only ever targets a record renders no url field, and then there is no url pane to
// offer a switch to.
function hasUrlField() {
  return Boolean(lightDomInput(props.urlInputSelector));
}

const selection = useTargetSelection({
  initial: { url: props.targetUrl, id: props.recordId, type: props.recordType, sparkleUrl: props.sparkleUrl },
  targetName: () => props.targetName,
  hasUrlField,
});
const { target, mode, actions } = selection;

const { preview } = useTargetPreview(() => target.sparkleUrl);

const previewUrl = computed(() => (toBoolean(props.linkToEdit) ? preview.editUrl : preview.url));

// The surrounding Rails form owns the field names, so the selection is mirrored into the inputs it
// rendered rather than submitted from here.
function mirrorTargetIntoForm() {
  const url = lightDomInput(props.urlInputSelector);
  const id = lightDomInput(props.idInputSelector);
  const type = lightDomInput(props.typeInputSelector);

  if (url) {
    url.value = target.url ?? "";
  }
  if (id) {
    id.value = target.id ?? "";
  }
  if (type) {
    type.value = target.type ?? "";
  }
}

watchImmediate(target, mirrorTargetIntoForm, { flush: "sync" });

function selectTarget(next) {
  selection.applyTarget(next);
  emit("select-target", { ...target });
}

function removeTarget() {
  selection.applyTarget({});
  emit("remove");
}

function showSearch() {
  selection.showSearch();
  emit("show-search");
}

function showCustomUrl() {
  selection.showCustomUrl();
  emit("show-custom-url");
}

// Abandoning a switch changes the pane, so it announces the pane it lands on the same way an
// explicit switch does.
function cancel() {
  selection.cancel();
  emit(mode.value === "url" ? "show-custom-url" : "show-search");
}

const actionHandlers = {
  change: showSearch,
  choose: showSearch,
  custom: showCustomUrl,
  remove: removeTarget,
  cancel,
};

function runAction(key) {
  actionHandlers[key]();
}

function onUrlInputChange(event) {
  selectTarget({ url: event.target.value });
}

// The record search is the admin-wide widget, server-rendered into the search slot, and it
// announces a pick through a callback rather than an event. It binds jQuery handlers to that
// light-DOM container and offers no teardown, and the container outlives this element, so it is
// bound once and the pick is routed through the host to reach whichever instance is mounted.
function connectRecordSearch() {
  const container = host?.querySelector(props.searchSelector);

  if (!container || typeof window.AdminSearch !== "function" || container.dataset.spTargetSearch) {
    return;
  }

  container.dataset.spTargetSearch = "connected";
  new window.AdminSearch(container, { form: false, selectCallback: (picked) => host.selectTarget(picked) });
}

// The url field is rendered by the surrounding Rails form, so it is resolved on mount.
const urlInput = shallowRef(null);

useEventListener(urlInput, "change", onUrlInputChange);

onMounted(() => {
  urlInput.value = lightDomInput(props.urlInputSelector);
  connectRecordSearch();
});

const { exposeMethods, exposeProperties } = useExpose();

exposeMethods({ selectTarget, removeTarget, showSearch, showCustomUrl, cancel });
exposeProperties({
  target: { get: () => ({ ...target }) },
  mode: { get: () => mode.value },
});
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-target-selector-app {
  display: block;

  &__pane {
    display: block;
    position: relative;
  }
}
</style>
